import {
  ActivityQuery,
  ControlCode,
  ControlCodes,
  defaultDateRange,
  isValid,
  PartnerEvents,
  Platforms,
  parseDate,
  ValidControlCategories,
  ValidControlCodes,
  ValidPartnerEvents,
} from "@theprelude/core";
import { z } from "zod";

export const dosSchema = z.enum(Platforms);
export const validControlSchema = z.nativeEnum(ValidControlCodes);
export const validControlCategorySchema = z.nativeEnum(ValidControlCategories);
export const controlSchema = z.nativeEnum(ControlCodes);
export const partnerEventSchema = z.nativeEnum(PartnerEvents);
export const validPartnerEventSchema = z.nativeEnum(ValidPartnerEvents);

export const controlInstanceSchema = z.object({
  instanceId: z.string().uuid(),
  control: validControlSchema,
});

export function isEmpty(value: unknown) {
  return value === undefined || value === null;
}

export function isPresent(value: unknown) {
  return !isEmpty(value);
}

export function isValidControl(value: ControlCode) {
  return validControlSchema.safeParse(value).success;
}

const isValidDate = (date?: string) => {
  if (!date) {
    return true;
  }
  return isValid(parseDate(date));
};

export const dateRangeSchema = z.object({
  finish: z
    .string()
    .optional()
    .catch(undefined)
    .refine(isValidDate, { message: "Invalid date" }),
  start: z
    .string()
    .optional()
    .catch(undefined)
    .refine(isValidDate, { message: "Invalid date" }),
});

export const activityQuerySchema = dateRangeSchema.extend({
  tests: z.array(z.string()).optional().catch(undefined),
  threats: z.array(z.string()).optional().catch(undefined),
  endpoints: z.array(z.string()).optional().catch(undefined),
  dos: dosSchema.optional().catch(undefined),
  os: z.string().optional().catch(undefined),
  policy: z.string().optional().catch(undefined),
  control: z.coerce.number().pipe(controlSchema).optional().catch(undefined),
});

export type ActivityQuerySchema = z.TypeOf<typeof activityQuerySchema>;

export const getActivityQuery = (
  state: ActivityQuerySchema = {},
): ActivityQuery => {
  return {
    start: state.start ?? defaultDateRange.start.toISOString(),
    finish: state.finish ?? defaultDateRange.finish.toISOString(),
    tests: state.tests?.join(","),
    threats: state.threats?.join(","),
    endpoints: state.endpoints?.join(","),
    os: state.os,
    dos: state.dos,
    policy: state.policy,
    control: state.control?.toString(),
  };
};

export const jsonStringSchema = z.string().refine(
  (value) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid JSON" },
);

export const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters long" })
  .refine((password) => /[a-z]/.test(password), {
    message: "Password must contain at least one lowercase letter",
  })
  .refine((password) => /[A-Z]/.test(password), {
    message: "Password must contain at least one uppercase letter",
  })
  .refine((password) => /\d/.test(password), {
    message: "Password must contain at least one number",
  })
  .refine((password) => /[^\dA-Za-z]/.test(password), {
    message: "Password must contain at least one special character",
  });
